import axios, { type AxiosInstance } from 'axios'
import { env } from 'next-runtime-env'
import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
} from 'react'

// Fallback to build-time env for server-side and as a safety on the client.
// IMPORTANT: Do not destructure process.env; access keys directly to allow Next.js to inline values.
const BUILD_TIME_API_URL =
  (typeof process !== 'undefined'
    ? env('NEXT_PUBLIC_ASSIDUOUS_API_URL')
    : '') || ''
const BUILD_TIME_ENV =
  (typeof process !== 'undefined' ? env('NEXT_PUBLIC_ENV') : 'development') ||
  'development'

// Create anonymous API instance without auth interceptors
const createAnonymousApiInstance = (baseURL: string) => {
  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 300000,
  })
}

// Create default api instance for server-side usage and fallback
const createApiInstance = (baseURL: string, env: string) => {
  console.log('🔧 Creating API instance:', {
    baseURL,
    env,
    source: 'createApiInstance',
  })

  const api = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true,
    timeout: 300000,
    xsrfCookieName: `csrf_access_token_${env}`,
    xsrfHeaderName: 'X-CSRF-TOKEN',
  })

  const refreshApi = axios.create({
    ...api.defaults,
    xsrfCookieName: `csrf_refresh_token_${env}`,
  })

  const anonymousApi = createAnonymousApiInstance(baseURL)

  console.log('🔧 RefreshAPI instance created:', {
    baseURL: refreshApi.defaults.baseURL,
    xsrfCookieName: refreshApi.defaults.xsrfCookieName,
  })

  // Setup interceptors (only for authenticated APIs)
  setupInterceptors(api, refreshApi)

  return { api, refreshApi, anonymousApi }
}

// Global refresh state to prevent multiple instances from refreshing simultaneously
let globalIsRefreshing = false
let globalFailedRequestsQueue: {
  resolve: (value?: unknown) => void
  reject: (reason) => void
}[] = []

const processGlobalQueue = (error) => {
  for (const prom of globalFailedRequestsQueue) {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  }
  globalFailedRequestsQueue = []
}

// Setup function for interceptors
function setupInterceptors(apiInstance, refreshInstance) {
  const refreshToken = async () => {
    // Use global refresh state to prevent multiple API instances from refreshing simultaneously
    if (globalIsRefreshing) {
      return new Promise((resolve, reject) => {
        globalFailedRequestsQueue.push({ resolve, reject })
      })
    }

    globalIsRefreshing = true

    try {
      await refreshInstance.post('/auth/refresh')
      processGlobalQueue(null)
    } catch (error) {
      console.error('❌ Token refresh failed:', error)
      processGlobalQueue(error)
      throw error
    } finally {
      globalIsRefreshing = false
    }
  }

  // Add retry logic for connection errors (dev mode only)
  // This helps with Windows Docker/WSL2 connection issues where requests may fail initially
  if (
    typeof process !== 'undefined' &&
    process.env.NODE_ENV === 'development'
  ) {
    apiInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config

        // Only retry on connection errors (ECONNRESET, ECONNREFUSED, ETIMEDOUT)
        // Don't retry if we've already retried or if it's a non-retryable error
        if (
          config &&
          !config._retryConnection &&
          (error.code === 'ECONNRESET' ||
            error.code === 'ECONNREFUSED' ||
            error.code === 'ETIMEDOUT' ||
            error.message === 'Network Error')
        ) {
          config._retryConnection = true
          // Exponential backoff: wait 1s, then 2s
          const retryCount = config._retryCount || 0
          if (retryCount < 2) {
            config._retryCount = retryCount + 1
            const delay = 1000 * (retryCount + 1)
            await new Promise((resolve) => setTimeout(resolve, delay))
            return apiInstance(config)
          }
        }
        return Promise.reject(error)
      }
    )
  }

  apiInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config
      if (error?.response?.status === 403) {
        // Do not hard-redirect on 403. Let pages/components handle access denied
        // (redirecting here breaks in-app flows by masking the actual error).
      } else if (
        error?.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes('/auth/login')
      ) {
        console.error(error)
        originalRequest._retry = true

        try {
          await refreshToken()
          return apiInstance(originalRequest)
        } catch (refreshError) {
          if (!window.location.pathname.includes('/login')) {
            const nextUrl = `${window.location.pathname}${
              window.location.search || ''
            }`
            window.location.href = `/login?next=${encodeURIComponent(nextUrl)}`
          }
          return Promise.reject(refreshError)
        }
      }
      return Promise.reject(error)
    }
  )
}

// Default instances for server-side and fallback
const {
  api: defaultApi,
  refreshApi: defaultRefreshApi,
  anonymousApi: defaultAnonymousApi,
} = createApiInstance(BUILD_TIME_API_URL, BUILD_TIME_ENV)

// Create context for the API instance
const ApiContext = createContext<{
  api: typeof defaultApi
  refreshApi: typeof defaultRefreshApi
  anonymousApi: typeof defaultAnonymousApi
}>({
  api: defaultApi,
  refreshApi: defaultRefreshApi,
  anonymousApi: defaultAnonymousApi,
})

// Cache for API instances to prevent recreation
const apiInstanceCache = new Map<
  string,
  { api: AxiosInstance; refreshApi: AxiosInstance; anonymousApi: AxiosInstance }
>()

// Provider component
export function ApiProvider({ children }: { children: ReactNode }) {
  const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')
  const NEXT_PUBLIC_ENV = env('NEXT_PUBLIC_ENV')
  const runtimeEnv = NEXT_PUBLIC_ENV
  const runtimeApiUrl = NEXT_PUBLIC_ASSIDUOUS_API_URL

  // Allow Playwright (or manual) override via query string: __apiBase
  // If provided without trailing /v1, add it for axios base
  let queryOverrideApiUrl: string | null = null
  if (typeof window !== 'undefined') {
    try {
      const currentUrl = new URL(window.location.href)
      const apiBaseParam = currentUrl.searchParams.get('__apiBase')
      if (apiBaseParam) {
        queryOverrideApiUrl = /\/?v1\/?$/.test(apiBaseParam)
          ? apiBaseParam.replace(/\/?$/, '') // ensure no trailing slash
          : `${apiBaseParam.replace(/\/?$/, '')}/v1`
      }
    } catch {
      // ignore URL parsing errors
    }
  }

  const lastInstancesRef = useRef<{
    api: AxiosInstance
    refreshApi: AxiosInstance
    anonymousApi: AxiosInstance
  } | null>(null)

  const apiInstances = useMemo(() => {
    // Determine if we have a valid env-provided URL for this navigation
    const envUrlFromRuntimeOrBuild =
      queryOverrideApiUrl || runtimeApiUrl || BUILD_TIME_API_URL
    const hasValidEnvUrl = Boolean(envUrlFromRuntimeOrBuild)
    const resolvedEnv = runtimeEnv || BUILD_TIME_ENV || 'development'

    // If env URL is not currently available, never read/write using an empty cache key
    if (!hasValidEnvUrl) {
      if (lastInstancesRef.current) {
        return lastInstancesRef.current
      }
      return {
        api: defaultApi,
        refreshApi: defaultRefreshApi,
        anonymousApi: defaultAnonymousApi,
      }
    }

    const cacheKey = `${envUrlFromRuntimeOrBuild}:${resolvedEnv}`

    // If we have a valid cache hit, return it
    if (apiInstanceCache.has(cacheKey)) {
      const cached = apiInstanceCache.get(cacheKey)
      if (cached) {
        lastInstancesRef.current = cached
        return cached
      }
    }

    const instances = createApiInstance(envUrlFromRuntimeOrBuild, resolvedEnv)
    apiInstanceCache.set(cacheKey, instances)
    lastInstancesRef.current = instances

    if (apiInstanceCache.size > 5) {
      const firstKey = apiInstanceCache.keys().next().value
      apiInstanceCache.delete(firstKey)
    }

    return instances
  }, [runtimeApiUrl, runtimeEnv, queryOverrideApiUrl])

  return (
    <ApiContext.Provider value={apiInstances}>{children}</ApiContext.Provider>
  )
}

// Hook to use the API context
export function useApiContext() {
  return useContext(ApiContext)
}

// For backward compatibility and server-side usage - export the default instance
const api = defaultApi
const refreshApi = defaultRefreshApi

// Legacy refresh token function for backward compatibility
let isRefreshing = false
let failedRequestsQueue: {
  resolve: (value?: unknown) => void
  reject: (reason: unknown) => void
}[] = []

const processQueue = (error) => {
  for (const prom of failedRequestsQueue) {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  }
  failedRequestsQueue = []
}

// DEPRECATED: Legacy refresh token function - DO NOT USE
// Use the refresh API from useApiContext() instead
export async function refreshToken() {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedRequestsQueue.push({ resolve, reject })
    })
  }

  isRefreshing = true

  try {
    await refreshApi.post('/auth/refresh')
    processQueue(null)
  } catch (error) {
    processQueue(error)
    throw error
  } finally {
    isRefreshing = false
  }
}

export default api
